import React from 'react';
import { Check, Star, ArrowRight } from 'lucide-react';

const Pricing: React.FC = () => {
  const plans = [
    {
      name: "Starter",
      price: "$299",
      period: "per month",
      description: "Perfect for small teams getting started with AWS security",
      features: [
        "Up to 5 AWS accounts",
        "Basic security scanning",
        "Weekly security reports",
        "Email notifications",
        "Community support",
        "CIS AWS Foundations compliance"
      ],
      cta: "Start Free Trial",
      popular: false,
      color: "border-gray-200"
    },
    {
      name: "Professional",
      price: "$899",
      period: "per month",
      description: "Advanced security for growing organizations",
      features: [
        "Up to 25 AWS accounts",
        "Full compliance monitoring",
        "Real-time monitoring & alerts",
        "Automated remediation (basic)",
        "API access & integrations",
        "Business hours support",
        "Slack/Teams notifications",
        "Multiple compliance frameworks"
      ],
      cta: "Start Free Trial",
      popular: true,
      color: "border-primary-500 ring-2 ring-primary-500"
    },
    {
      name: "Enterprise",
      price: "$2,999",
      period: "per month",
      description: "Complete security platform for large organizations",
      features: [
        "Unlimited AWS accounts",
        "Advanced automation & AI",
        "Custom integrations",
        "24/7 premium support",
        "Dedicated customer success manager",
        "Custom compliance policies",
        "White-label options",
        "Professional services"
      ],
      cta: "Contact Sales",
      popular: false,
      color: "border-gray-200"
    }
  ];

  const features = [
    "AWS Well-Architected Framework Integration",
    "AI-Powered Security Analysis",
    "Real-Time Configuration Monitoring",
    "Automated Evidence Collection",
    "Cross-Account Security Analysis",
    "Infrastructure-as-Code Integration",
    "Advanced Risk Scoring",
    "Compliance Reporting & Dashboards"
  ];

  return (
    <section id="pricing" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Choose the plan that fits your organization's needs. All plans include 
            our core security features with no hidden fees.
          </p>
          
          {/* Pricing Toggle */}
          <div className="inline-flex items-center bg-white rounded-lg p-1 shadow-sm">
            <button className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md">
              Monthly
            </button>
            <button className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700">
              Annual (Save 20%)
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {plans.map((plan, index) => (
            <div 
              key={index}
              className={`bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow relative ${plan.color}`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-primary-600 text-white px-4 py-2 rounded-full text-sm font-medium flex items-center space-x-1">
                    <Star className="h-4 w-4" />
                    <span>Most Popular</span>
                  </div>
                </div>
              )}
              
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <p className="text-gray-600 mb-4">{plan.description}</p>
                <div className="mb-4">
                  <span className="text-5xl font-bold text-gray-900">{plan.price}</span>
                  <span className="text-gray-600 ml-2">{plan.period}</span>
                </div>
              </div>

              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start space-x-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>

              <button 
                className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
                  plan.popular 
                    ? 'bg-primary-600 text-white hover:bg-primary-700' 
                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                }`}
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>

        {/* All Plans Include */}
        <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-sm">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
            All Plans Include
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center space-x-3">
                <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="text-gray-600">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Enterprise CTA */}
        <div className="mt-16 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-2xl p-8 lg:p-12 text-white text-center">
          <h3 className="text-3xl font-bold mb-4">
            Need a Custom Solution?
          </h3>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            For organizations with unique requirements, we offer custom deployments, 
            on-premises solutions, and dedicated professional services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2">
              <span>Contact Sales</span>
              <ArrowRight className="h-5 w-5" />
            </button>
            <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-colors">
              Schedule Demo
            </button>
          </div>
        </div>

        {/* FAQ Preview */}
        <div className="mt-16 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Questions About Pricing?
          </h3>
          <p className="text-lg text-gray-600 mb-6">
            Our team is here to help you choose the right plan for your organization.
          </p>
          <div className="inline-flex items-center space-x-6 text-sm text-gray-500">
            <span>✓ 14-day free trial</span>
            <span>✓ No setup fees</span>
            <span>✓ Cancel anytime</span>
            <span>✓ 24/7 support</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
